/**
 * AI Check generation (route `/checks/generate`; Feature: ai-check-generation, task 23.3;
 * Requirements 9.1, 9.2, 9.6, 9.7; Design: §Components 11).
 *
 * Submit one Source_Document — a file, a public URL, or pasted text — then watch the Generation_Request
 * it created until it is done, and hand off to the draft review page.
 *
 * Four things this page is careful about:
 *
 *  - **Exactly one source mode, structurally.** The three modes are tabs over a single `mode` state
 *    value, and only the selected tab's control is rendered, so there is no reachable UI state in which
 *    two modes carry input. {@link buildSource} then switches on that one value and returns one arm of
 *    {@link GenerationSourcePayload}, whose `?: never` siblings make a two-mode body fail to typecheck.
 *    Requirement 1.2's "exactly one mode" is therefore unreachable from here rather than validated on
 *    the way out — the server still rejects a two-mode body, but nothing this page can do produces one.
 *
 *  - **Progress comes from {@link useGenerationRequest}** (Req 9.2), which polls while the request is
 *    non-terminal and stops at `succeeded` / `failed` / `timed_out`. The indicator is shown for exactly
 *    the union of "submitting" and "polling a non-terminal request", so it appears the moment the user
 *    acts and clears the moment the request is done.
 *
 *  - **A retry never re-uploads** (Req 9.6). The exact {@link GenerationSourcePayload} that was
 *    submitted is kept in component state, and retry resubmits *that value* — it calls
 *    `createGenerationRequest` and nothing else. The upload steps (`createSourceDocumentUploadUrl`,
 *    then the presigned PUT) live only in {@link buildSource}, which the retry path does not call, so
 *    "no second upload" is a property of the call graph rather than a condition to remember. For an
 *    uploaded document that retained payload is `{ documentKey }` — the object is already in the
 *    Source_Document_Store, so there is nothing to send again.
 *
 *  - **A viewer sees the flow read-only** (Req 9.7) through the existing `use-can-write` hook: no tabs,
 *    no inputs, no generate action. Display only — the API's role matrix is the enforcement point.
 *
 * The presigned PUT goes through the standalone {@link uploadSourceDocument}, never a hand-rolled
 * `fetch`: it targets another origin, so it must carry no bearer token, and its headers are the
 * *signed* `requiredHeaders` echoed verbatim rather than recomputed.
 *
 * ## Which account will process the document (Feature: customer-managed-inference, task 18.1)
 *
 * customer-managed-inference **Requirements 13.8 and 17.8**; Design: §Route table's note on
 * Requirement 13.8, §Design-level decisions ("Found unimplementable" item 2).
 *
 * A document submitted here may be processed in Soroush's AWS account or in the customer's own, and
 * Requirement 13.8 wants the person submitting it to know *which* before they do. Everything else on
 * this page is unchanged: one indication is added and no existing behaviour moves (Requirement 17.8).
 *
 * **Pre-submit the indication is derived, not read, and that is the design's resolution of a
 * requirement that cannot be met as written.** Every `inference-config:*` action is admin-only
 * (Requirements 14.2, 14.3), so there is no editor-readable configuration read; Requirement 17.6
 * permits no sixth route to add one. So {@link useDerivedInferenceMode} takes the mode of the Account's
 * **most recent Generation_Request** — `GET /v1/generation-requests` is editor-readable and now carries
 * `inferenceMode` on every item — and states the platform default with an explicit hedge when there is
 * no prior request to read.
 *
 * **Post-submit it is exact**, from the `inferenceMode` on the submit response, which is the half no
 * read model can cover: on an Account's very first generation there is no prior request at all.
 *
 * **The residual imprecision is accepted, and naming it here is the point.** An Account that has
 * configured `customer_hosted` and never generated is shown the hedged default, which is *not* what
 * will happen. Widening `inference-config:read` to editor would close the gap — and would also hand
 * every editor the External_Id and the customer's AWS account id, to fix a display. Adding a route is
 * forbidden outright. So this is a known, bounded wrongness with an explicit hedge over it, and it
 * should not be "fixed" by either of those two means.
 *
 * **The indication can never break generation.** A failed or unreadable history read degrades to the
 * hedged default rather than to an error panel, because Requirement 17.8 keeps the flow otherwise
 * unchanged and a decoration must not stand between an operator and a submission.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { uploadSourceDocument } from '../api/client';
import { useApiClient } from '../api/use-api-client';
import type {
  CreateGenerationRequestPayload,
  GenerationFailureDto,
  GenerationRequestDto,
  GenerationRequestState,
  GenerationSourcePayload,
  InferenceFailureDto,
  InferenceFailureKind,
  InferenceMode,
  SourceDocumentRefDto,
} from '../api/types';
import { useCanWrite } from '../auth/use-can-write';
import { ErrorState } from '../components/LoadStates';
// One spelling of the mode names and of the remediation sentences, shared with the Inference
// configuration page (task 17.3) rather than respelled here.
import {
  FAILURE_LABELS,
  FAILURE_REMEDIATION,
  MODE_LABELS,
  isUnjudgedFailure,
} from '../lib/inference-copy';
import { useGenerationRequest, type GenerationRequestPoll } from '../lib/use-generation-request';
import { useWrite } from '../lib/use-write';

/* -------------------------------------------------------------------------- */
/* Source modes                                                                */
/* -------------------------------------------------------------------------- */

/** The three Source_Document modes (Req 9.1). One value, so at most one can ever be selected. */
type SourceMode = 'file' | 'url' | 'text';

const MODE_TABS: readonly { mode: SourceMode; label: string }[] = [
  { mode: 'file', label: 'Upload a file' },
  { mode: 'url', label: 'Fetch a URL' },
  { mode: 'text', label: 'Paste text' },
];

/** A file with no declared media type still needs one, because the presign signs it. */
const FALLBACK_CONTENT_TYPE = 'application/octet-stream';

/* -------------------------------------------------------------------------- */
/* Which account will process the document (Requirements 13.8, 17.8)           */
/* -------------------------------------------------------------------------- */

/**
 * What this page can honestly say about the Inference_Mode *before* anything is submitted.
 *
 * Three states rather than an optional mode, because "we have not looked yet" and "there is nothing to
 * look at" produce different copy and only the second one is a statement about the Account:
 *
 *  - `reading` — the history read is in flight. The region renders, and says so, rather than showing a
 *    hedge it is about to replace; the region existing from first paint is also what makes the later
 *    change announceable in a live region.
 *  - `default` — no prior Generation_Request, or one that carries no mode, or a read that failed. All
 *    three collapse into the same honest sentence: the platform default, hedged.
 *  - `derived` — the mode of the Account's most recent Generation_Request.
 */
type DerivedInferenceMode =
  | { status: 'reading' }
  | { status: 'default' }
  | { status: 'derived'; mode: InferenceMode };

/**
 * The Inference_Mode of the Account's most recent Generation_Request, or `default` when there is none
 * (Requirement 13.8; Design: §Route table's note on Requirement 13.8).
 *
 * One read, on mount, of `GET /v1/generation-requests` — the editor-readable list, in chronological
 * order, so the most recent request is its **last** element. The page uses one field of one item; the
 * cost is deliberate and is the price of not adding a route Requirement 17.6 forbids.
 *
 * `enabled` is the write gate, and it is a gate **ahead of the fetch** rather than a branch after it,
 * matching the configuration page: a viewer cannot generate, so there is nothing for the indication to
 * be about and no request is issued for it.
 *
 * **A failure resolves to `default`, deliberately.** This is a decoration on a flow Requirement 17.8
 * keeps otherwise unchanged; a `403`, a `500`, or a network blip must not put an error panel between an
 * operator and a submission. The post-submit confirmation is exact regardless, because it comes from
 * the submit response rather than from here.
 */
function useDerivedInferenceMode(enabled: boolean): DerivedInferenceMode {
  const client = useApiClient();
  const [derived, setDerived] = useState<DerivedInferenceMode>({ status: 'reading' });

  useEffect(() => {
    if (!enabled) return undefined;

    // The same teardown guard `useResource` uses: a response from a torn-down effect must not land.
    let active = true;
    client
      .listGenerationRequests()
      .then((response) => {
        if (!active) return;
        const requests = response.generationRequests;
        // Chronological order, so the most recent request is the last one.
        const mode = requests[requests.length - 1]?.inferenceMode;
        setDerived(mode === undefined ? { status: 'default' } : { status: 'derived', mode });
      })
      .catch(() => {
        if (active) setDerived({ status: 'default' });
      });

    return () => {
      active = false;
    };
  }, [client, enabled]);

  return derived;
}

/** The accessible name of the mode region, so the disclosure is labelled rather than merely placed. */
const MODE_NOTICE_LABEL = 'Where this document is processed';

/**
 * Which account will process the document: derived before submission, exact after it
 * (Requirements 13.8, 17.8).
 *
 * A `role="status"` region present from first paint, so the pre-submit statement is available to a
 * screen reader on arrival *and* the post-submit confirmation is announced when it replaces it. The
 * meaning is carried entirely by the sentence — no colour, no icon, no position — and the region
 * carries its own accessible name.
 *
 * `submitted` wins whenever it exists: it is the mode the platform actually stamped on the request,
 * read off the `202`, and it is right even on an Account's first ever generation.
 */
function InferenceModeNotice({
  derived,
  submitted,
}: {
  derived: DerivedInferenceMode;
  submitted: InferenceMode | undefined;
}): JSX.Element {
  return (
    <div
      role="status"
      aria-label={MODE_NOTICE_LABEL}
      className="muted"
      data-testid="inference-mode-notice"
    >
      {submitted !== undefined ? (
        <p data-testid="inference-mode-confirmed">
          This document is being processed in <strong>{MODE_LABELS[submitted]}</strong> inference.
        </p>
      ) : derived.status === 'reading' ? (
        <p>Checking where this account’s documents are processed…</p>
      ) : derived.status === 'derived' ? (
        <p data-testid="inference-mode-derived">
          This account’s most recent generation ran in <strong>{MODE_LABELS[derived.mode]}</strong>{' '}
          inference, and this document will be processed the same way unless an administrator has
          changed the configuration since.
        </p>
      ) : (
        <p data-testid="inference-mode-default">
          Documents are processed in Soroush’s own AWS account, unless an administrator has configured
          customer-hosted inference. This account has no earlier generation to report.
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Rendering the request's progress and outcome                                */
/* -------------------------------------------------------------------------- */

const STATE_LABELS: Readonly<Record<GenerationRequestState, string>> = {
  queued: 'Queued',
  fetching: 'Fetching the document',
  extracting: 'Extracting text',
  generating: 'Generating the check',
  validating: 'Validating the generated check',
  succeeded: 'Succeeded',
  failed: 'Failed',
  timed_out: 'Timed out',
};

function asNumber(value: unknown): string {
  return typeof value === 'number' ? String(value) : 'an unrecorded value';
}

function asText(value: unknown): string {
  return typeof value === 'string' && value.length > 0 ? value : 'unrecorded';
}

function asList(value: unknown): string {
  return Array.isArray(value) ? value.map((entry) => String(entry)).join(', ') : 'unrecorded';
}

/** Why a URL was refused, read defensively out of the nested `UrlRejection`. */
function describeRejection(value: unknown): string {
  if (value === null || typeof value !== 'object') return 'the URL policy refused it';
  const rejection = value as Record<string, unknown>;
  switch (rejection.kind) {
    case 'rejected_scheme':
      return `the scheme ${asText(rejection.scheme)} is not allowed`;
    case 'rejected_port':
      return `the port ${asNumber(rejection.port)} is not allowed`;
    case 'blocked_address':
      return `the address ${asText(rejection.address)} is blocked (${asText(rejection.reason)})`;
    default:
      return 'the URL policy refused it';
  }
}

/**
 * One operator-readable sentence per failure arm (Req 9.6). The discriminant is switched on
 * exhaustively; each arm's own datum is read through {@link GenerationFailureDto}'s index signature,
 * which is how that DTO deliberately models the thirteen payloads without mirroring them.
 */
export function describeGenerationFailure(failure: GenerationFailureDto): string {
  switch (failure.kind) {
    case 'fetch_rejected':
      return `The URL was refused before any connection was opened: ${describeRejection(failure.rejection)}.`;
    case 'fetch_redirect_limit':
      return `The URL redirected more than the permitted ${asNumber(failure.maxRedirects)} times.`;
    case 'fetch_too_large':
      return `The document is larger than the permitted ${asNumber(failure.maxBytes)} bytes.`;
    case 'fetch_timeout':
      return `Fetching the document took longer than ${asNumber(failure.timeoutMs)} ms.`;
    case 'unsupported_content_type':
      return `The document type ${asText(failure.detected)} is not supported. Supported types: ${asList(failure.supported)}.`;
    case 'extraction_timeout':
      return `Extracting text took longer than ${asNumber(failure.timeoutMs)} ms.`;
    case 'no_text':
      return 'The document contained no extractable text.';
    case 'input_too_large':
      return `The document is too large to generate from: about ${asNumber(failure.estimatedTokens)} tokens against a limit of ${asNumber(failure.effectiveLimitTokens)}.`;
    case 'profile_unavailable':
      return `The configured model ${asText(failure.inferenceProfileId)} is not available in this region.`;
    case 'validation_failed':
      return `The generated check still had ${asNumber(failure.remainingErrorCount)} validation error(s) after the correction budget was spent.`;
    case 'model_throttled':
      return `The model was throttled and did not recover after ${asNumber(failure.retriesAttempted)} retry attempt(s).`;
    case 'model_error':
      return `The model call failed with ${asText(failure.errorName)}.`;
    case 'generation_timeout':
      return `Generation did not finish within the ${asNumber(failure.budgetSeconds)}-second budget.`;
    case 'inference_failed':
      // No model was ever reached, so nothing here is about the document. The nested InferenceFailure
      // is also hoisted onto the request, where `InferenceFailureNotice` states what to change.
      return `Generation never reached a model: ${describeInferenceArm(failure.failure)}.`;
    default:
      return `Generation failed (${String(failure.kind)}).`;
  }
}

/**
 * The `inference_failed` arm's own datum, named through the shared {@link FAILURE_LABELS}.
 *
 * Read defensively out of {@link GenerationFailureDto}'s index signature and total over `unknown`, the
 * same discipline {@link describeRejection} applies to `UrlRejection`. `Object.hasOwn` rather than `in`,
 * so a `kind` of `'toString'` does not resolve to a prototype member and render `[object Function]`.
 */
function describeInferenceArm(value: unknown): string {
  if (value !== null && typeof value === 'object') {
    const kind = (value as Record<string, unknown>).kind;
    if (typeof kind === 'string' && Object.hasOwn(FAILURE_LABELS, kind)) {
      return FAILURE_LABELS[kind as InferenceFailureKind];
    }
  }
  return 'the configured inference could not be used';
}

/** The failure line for a terminal request, whatever the server recorded. */
function terminalFailureMessage(request: GenerationRequestDto): string {
  const { outcome } = request;
  if (outcome !== undefined && outcome.kind === 'failed') return describeGenerationFailure(outcome.failure);
  return request.state === 'timed_out'
    ? 'Generation did not finish within the configured budget.'
    : 'Generation failed without a recorded reason.';
}

/** The retained Source_Document, described for the human deciding whether to retry (Req 9.6). */
function describeSourceRef(ref: SourceDocumentRefDto): string {
  switch (ref.mode) {
    case 'upload':
      return `uploaded document (${ref.contentType}, ${ref.byteLength} bytes)`;
    case 'url':
      return ref.url;
    case 'pasted':
      return `pasted text (${ref.characterCount} characters)`;
  }
}

/* -------------------------------------------------------------------------- */
/* The page                                                                    */
/* -------------------------------------------------------------------------- */

export interface CheckGeneratePageProps {
  /**
   * Poll cadence, forwarded to {@link useGenerationRequest}. Injectable so a component test can drive
   * several polls without waiting on wall clock; production uses the hook's default.
   */
  pollIntervalMs?: number;
}

export function CheckGeneratePage({ pollIntervalMs }: CheckGeneratePageProps = {}): JSX.Element {
  const client = useApiClient();
  const canWrite = useCanWrite();
  const write = useWrite();

  const [mode, setMode] = useState<SourceMode>('file');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [url, setUrl] = useState('');
  const [pastedText, setPastedText] = useState('');

  const [projectId, setProjectId] = useState('');
  const [logicalName, setLogicalName] = useState('');
  const [locations, setLocations] = useState('');
  const [hint, setHint] = useState('');

  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  /**
   * The exact body that was submitted, kept so a retry can resubmit it verbatim (Req 9.6). For an
   * uploaded document this is `{ documentKey }`, which is why retrying costs no second upload.
   */
  const [submittedSource, setSubmittedSource] = useState<GenerationSourcePayload | undefined>(undefined);
  /**
   * The Inference_Mode the platform stamped on the request it created, off the `202`
   * (customer-managed-inference Requirement 13.8). Exact, unlike the pre-submit derivation, and
   * re-stated on a retry because a retry creates a fresh Generation_Request that resolves the mode
   * again — an administrator may have changed it in between.
   */
  const [submittedMode, setSubmittedMode] = useState<InferenceMode | undefined>(undefined);

  const poll = useGenerationRequest(requestId, { pollIntervalMs });
  // Read before the read-only early return below, because hooks cannot live behind a branch; the `write`
  // gate is what keeps a viewer from issuing the read at all.
  const derivedMode = useDerivedInferenceMode(canWrite);

  /** The first-party guidance, omitting anything the operator left blank. */
  function guidance(): Omit<CreateGenerationRequestPayload, keyof GenerationSourcePayload> {
    const locationList = locations
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
    return {
      ...(projectId.trim().length > 0 ? { projectId: projectId.trim() } : {}),
      ...(logicalName.trim().length > 0 ? { logicalName: logicalName.trim() } : {}),
      ...(locationList.length > 0 ? { locations: locationList } : {}),
      ...(hint.trim().length > 0 ? { hint: hint.trim() } : {}),
    };
  }

  /**
   * Turn the selected tab into one source arm — the **only** place an upload happens. A `file` mode
   * submission presigns, PUTs, and then carries the platform-minted `documentKey`; the other two modes
   * need no round trip at all.
   */
  async function buildSource(): Promise<GenerationSourcePayload> {
    switch (mode) {
      case 'file': {
        if (file === undefined) throw new Error('Choose a file to upload first.');
        const presigned = await client.createSourceDocumentUploadUrl({
          contentType: file.type.length > 0 ? file.type : FALLBACK_CONTENT_TYPE,
          contentLength: file.size,
          filename: file.name,
        });
        // Echoes `presigned.requiredHeaders` and sends no credential; see uploadSourceDocument.
        await uploadSourceDocument(presigned, file);
        return { documentKey: presigned.documentKey };
      }
      case 'url':
        return { url: url.trim() };
      case 'text':
        return { text: pastedText };
    }
  }

  /**
   * Submit one Generation_Request. `makeSource` is the only difference between a first attempt (which
   * may upload) and a retry (which resubmits the retained reference), so the retry path provably issues
   * no upload calls.
   */
  function submit(makeSource: () => Promise<GenerationSourcePayload>): void {
    void write.run(
      async () => {
        const source = await makeSource();
        const response = await client.createGenerationRequest({ ...source, ...guidance() });
        return { source, response };
      },
      ({ source, response }) => {
        setSubmittedSource(source);
        setRequestId(response.generationRequestId);
        // Absent only on a response that predates the field; the pre-submit statement then stands.
        setSubmittedMode(response.inferenceMode);
      },
    );
  }

  function onGenerate(event: FormEvent): void {
    event.preventDefault();
    submit(buildSource);
  }

  function onRetry(): void {
    if (submittedSource === undefined) return;
    const retained = submittedSource;
    submit(() => Promise.resolve(retained));
  }

  /** Whether the selected mode actually has input; keeps an empty submission out of the API. */
  const hasSource =
    mode === 'file' ? file !== undefined : mode === 'url' ? url.trim().length > 0 : pastedText.length > 0;

  const inProgress =
    write.state.submitting || poll.status === 'loading' || (poll.status === 'loaded' && !poll.terminal);

  if (!canWrite) {
    return (
      <section>
        <h1>Generate a check</h1>
        <p role="note" className="muted" data-testid="read-only-notice">
          Your role has read-only access, so checks cannot be generated from the dashboard. Generated
          drafts for this account are still visible under <Link to="/check-drafts">check drafts</Link>.
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="section-header">
        <h1>Generate a check</h1>
        <Link to="/check-drafts">Check drafts</Link>
      </div>
      <p className="muted">
        Provide API documentation one way — a file, a public URL, or pasted text — and the platform
        drafts a check from it for you to review before it goes live.
      </p>

      {/* Which account processes the document: derived before submission, exact after it (Req 13.8). */}
      <InferenceModeNotice derived={derivedMode} submitted={submittedMode} />

      <form onSubmit={onGenerate}>
        <SourceModeTabs mode={mode} onChange={setMode} disabled={inProgress} />
        <SourceModePanel
          mode={mode}
          disabled={inProgress}
          file={file}
          onFile={setFile}
          url={url}
          onUrl={setUrl}
          text={pastedText}
          onText={setPastedText}
        />

        <fieldset className="form-section">
          <legend>Guidance (optional)</legend>
          <div className="form-field">
            <label htmlFor="generate-project">Project</label>
            <input
              id="generate-project"
              type="text"
              value={projectId}
              disabled={inProgress}
              onChange={(event) => setProjectId(event.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="generate-name">Name</label>
            <input
              id="generate-name"
              type="text"
              value={logicalName}
              disabled={inProgress}
              onChange={(event) => setLogicalName(event.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor="generate-locations">Locations</label>
            <input
              id="generate-locations"
              type="text"
              value={locations}
              disabled={inProgress}
              onChange={(event) => setLocations(event.target.value)}
            />
            <p className="field-hint muted">Comma separated; the deployment default is used if blank.</p>
          </div>
          <div className="form-field">
            <label htmlFor="generate-hint">Hint</label>
            <input
              id="generate-hint"
              type="text"
              value={hint}
              disabled={inProgress}
              onChange={(event) => setHint(event.target.value)}
            />
          </div>
        </fieldset>

        <div className="form-actions">
          <button type="submit" disabled={inProgress || !hasSource}>
            Generate check
          </button>
        </div>
      </form>

      {/* A submit failure (including a failed upload) — the request never started. */}
      {write.state.errorMessage !== undefined ? (
        <ErrorState
          summary="Could not start generation."
          message={write.state.errorMessage}
          isAuthError={false}
        />
      ) : null}

      <GenerationProgress submitting={write.state.submitting} poll={poll} />
      <GenerationOutcome
        poll={poll}
        onRetry={onRetry}
        canRetry={submittedSource !== undefined}
        inProgress={inProgress}
      />
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Source mode tabs                                                            */
/* -------------------------------------------------------------------------- */

function tabId(mode: SourceMode): string {
  return `source-mode-${mode}`;
}

/**
 * The mode selector. One `mode` value in, one `onChange` out — there is no per-tab "enabled" state that
 * could put two modes on at once (Req 9.1).
 */
function SourceModeTabs({
  mode,
  onChange,
  disabled,
}: {
  mode: SourceMode;
  onChange: (mode: SourceMode) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="range-selector" role="tablist" aria-label="Source document">
      {MODE_TABS.map((tab) => (
        <button
          key={tab.mode}
          id={tabId(tab.mode)}
          type="button"
          role="tab"
          aria-selected={tab.mode === mode}
          aria-controls="source-mode-panel"
          className={tab.mode === mode ? 'range-option range-active' : 'range-option'}
          disabled={disabled}
          onClick={() => onChange(tab.mode)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The selected mode's control, and only that one: the other two modes have no input in the document at
 * all, so a user cannot populate two sources. Their typed values survive in the page's state, so
 * switching tabs and back does not lose work — but only the selected mode is ever read at submit.
 */
function SourceModePanel({
  mode,
  disabled,
  file,
  onFile,
  url,
  onUrl,
  text,
  onText,
}: {
  mode: SourceMode;
  disabled: boolean;
  file: File | undefined;
  onFile: (file: File | undefined) => void;
  url: string;
  onUrl: (url: string) => void;
  text: string;
  onText: (text: string) => void;
}): JSX.Element {
  return (
    <div role="tabpanel" id="source-mode-panel" aria-labelledby={tabId(mode)} className="form-section">
      {mode === 'file' ? (
        <div className="form-field">
          <label htmlFor="source-file">Documentation file</label>
          <input
            id="source-file"
            type="file"
            disabled={disabled}
            onChange={(event) => onFile(event.target.files?.[0])}
          />
          {file !== undefined ? (
            <p className="field-hint muted">
              {file.name} ({file.size} bytes)
            </p>
          ) : null}
        </div>
      ) : null}

      {mode === 'url' ? (
        <div className="form-field">
          <label htmlFor="source-url">Documentation URL</label>
          <input
            id="source-url"
            type="text"
            value={url}
            disabled={disabled}
            onChange={(event) => onUrl(event.target.value)}
          />
          <p className="field-hint muted">Public http or https URL; the platform fetches it server-side.</p>
        </div>
      ) : null}

      {mode === 'text' ? (
        <div className="form-field">
          <label htmlFor="source-text">Pasted documentation</label>
          <textarea
            id="source-text"
            rows={10}
            value={text}
            disabled={disabled}
            onChange={(event) => onText(event.target.value)}
          />
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Progress and outcome                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The progress indication Requirement 9.2 asks for: present while the request is in progress, absent
 * once it is not. "In progress" is the submit round trip plus every non-terminal poll.
 */
function GenerationProgress({
  submitting,
  poll,
}: {
  submitting: boolean;
  poll: GenerationRequestPoll;
}): JSX.Element | null {
  if (submitting) {
    return (
      <p role="status" className="muted" data-testid="generation-progress">
        Submitting the source document…
      </p>
    );
  }
  if (poll.status === 'loading') {
    return (
      <p role="status" className="muted" data-testid="generation-progress">
        Generating… reading the request.
      </p>
    );
  }
  if (poll.status === 'loaded' && !poll.terminal) {
    return (
      <p role="status" className="muted" data-testid="generation-progress">
        Generating… {STATE_LABELS[poll.generationRequest.state]}.
      </p>
    );
  }
  return null;
}

/** What a finished (or unreadable) Generation_Request shows. */
function GenerationOutcome({
  poll,
  onRetry,
  canRetry,
  inProgress,
}: {
  poll: GenerationRequestPoll;
  onRetry: () => void;
  canRetry: boolean;
  /**
   * A submission or a poll is in flight.
   *
   * Load-bearing, not cosmetic: during a retry this panel is still rendered from the *previous*
   * terminal poll until the effect re-runs against the new identifier, so the retry button coexists
   * with the progress indicator for a tick. Without this guard a second click issues a second
   * Generation_Request for one intent — two requests, one of which the page then never shows. The
   * Generate button was already guarded by the same value; this is the other half of it.
   */
  inProgress: boolean;
}): JSX.Element | null {
  // The poll itself failed — the request may well still be running, so this is not a generation failure.
  if (poll.status === 'error') {
    return (
      <ErrorState
        summary="Could not read the generation request."
        message={poll.message}
        isAuthError={poll.isAuthError}
      />
    );
  }

  if (poll.status !== 'loaded' || !poll.terminal) return null;

  const request = poll.generationRequest;

  if (request.state === 'succeeded') {
    return (
      <div data-testid="generation-succeeded">
        <p role="status">
          Generation succeeded after {request.correctionAttempts} self-correction round(s).
        </p>
        {poll.draft !== undefined ? (
          <p>
            <Link to={`/check-drafts/${encodeURIComponent(poll.draft.draftId)}`}>
              Review the generated draft
            </Link>
          </p>
        ) : (
          <p className="muted">The draft is no longer available to review.</p>
        )}
      </div>
    );
  }

  // `failed` and `timed_out`: display the returned error and keep the retry affordance (Req 9.6).
  return (
    <div data-testid="generation-failed">
      <div role="alert" className="error-panel">
        <p>{terminalFailureMessage(request)}</p>
        <p className="muted" data-testid="retained-source">
          Source document: {describeSourceRef(request.sourceDocument)}
        </p>
      </div>

      {/* Present exactly when resolution never reached a model (customer-managed-inference Req 7.4). */}
      {request.inferenceFailure !== undefined ? (
        <InferenceFailureNotice failure={request.inferenceFailure} />
      ) : null}

      {poll.draft !== undefined ? (
        // A correction-exhausted draft is persisted carrying its outstanding errors, so it is worth
        // reviewing even though generation failed. A `timed_out` request never has one.
        <p>
          <Link to={`/check-drafts/${encodeURIComponent(poll.draft.draftId)}`}>
            Review the draft and its validation errors
          </Link>
        </p>
      ) : (
        <p className="muted" data-testid="generation-no-draft">
          No draft was saved for this request, so there is nothing to review.
        </p>
      )}

      {canRetry ? (
        <div className="form-actions">
          <button type="button" onClick={onRetry} disabled={inProgress}>
            Retry generation
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The Inference_Failure on a generation request (Requirements 7.4, 7.6, 7.9)  */
/* -------------------------------------------------------------------------- */

/** The id the failure region is named by; one instance renders at a time, so one id suffices. */
const INFERENCE_FAILURE_HEADING_ID = 'generation-inference-failure-heading';

/**
 * Why resolution never reached a model, and what the customer changes in **their own** AWS account to
 * clear it (Requirement 7.4).
 *
 * ## The copy is shared, not respelled
 *
 * {@link FAILURE_LABELS} and {@link FAILURE_REMEDIATION} come from `lib/inference-copy`, the same tables
 * the Inference configuration page renders (task 17.3). A remediation sentence is an instruction about
 * somebody's own account, and two versions of it would be two versions of what they are being asked to
 * change. Four of those sentences say "below", because they name a document or a control that lives on
 * the configuration page — so this region renders a pointer at `/inference` beside the sentence rather
 * than editing the sentence. That link is useful to an editor too, even though the page there will tell
 * them the configuration belongs to an administrator: it answers "where does this get fixed, and by
 * whom".
 *
 * ## What it structurally cannot render
 *
 * No External_Id, no credential, and no provider **message** (Requirements 7.6, 7.9) — and that is the
 * shape of {@link InferenceFailureDto} rather than restraint at this call site: it declares neither a
 * `message` nor an `externalId`, because the server-side type declares neither. Bedrock and STS messages
 * routinely quote the request that provoked them, and here that request carries the customer's own API
 * documentation. The exception **class** is the whole of what may be shown.
 *
 * ## `probe_throttled` is not a fault
 *
 * A throttled probe means the platform could not reach a verdict, so it renders as a `role="status"`
 * saying "not yet verified" rather than a `role="alert"` saying "broken" — the same distinction the
 * configuration page draws, from the same shared predicate. There is no verify action here: verification
 * is admin-only (Requirements 14.2, 14.3), so offering it on a surface an editor uses would offer a
 * button that answers `403`.
 */
function InferenceFailureNotice({ failure }: { failure: InferenceFailureDto }): JSX.Element {
  const unjudged = isUnjudgedFailure(failure.kind);
  return (
    <div
      // Announced at the right urgency, and named by its own heading rather than by its position.
      role={unjudged ? 'status' : 'alert'}
      aria-labelledby={INFERENCE_FAILURE_HEADING_ID}
      className={unjudged ? 'card' : 'error-panel'}
      data-testid="generation-inference-failure"
    >
      <h2 id={INFERENCE_FAILURE_HEADING_ID}>
        {unjudged ? 'Inference not yet verified' : 'Inference configuration fault'}
      </h2>
      <p data-testid="generation-failure-kind">{FAILURE_LABELS[failure.kind]}</p>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Failure</dt>
          <dd>{failure.kind}</dd>
        </div>
        <div className="detail-row">
          <dt>Customer role</dt>
          <dd data-testid="generation-failure-role-arn">{failure.customerRoleArn ?? 'not reached'}</dd>
        </div>
        <div className="detail-row">
          <dt>Region</dt>
          <dd data-testid="generation-failure-region">{failure.region ?? 'not reached'}</dd>
        </div>
        <div className="detail-row">
          <dt>Inference profile</dt>
          <dd data-testid="generation-failure-profile-id">
            {failure.inferenceProfileId ?? 'not reached'}
          </dd>
        </div>
        <div className="detail-row">
          <dt>Provider error class</dt>
          {/* The class, never the message: a provider message quotes the request that provoked it, and
              here that request carries the customer's own documentation (Requirement 7.9). */}
          <dd data-testid="generation-failure-error-class">{failure.errorClass ?? 'none reported'}</dd>
        </div>
      </dl>
      <h3>What to change in your account</h3>
      <p data-testid="generation-failure-remediation">{FAILURE_REMEDIATION[failure.kind]}</p>
      <p className="muted">
        The documents and controls this refers to are on the{' '}
        <Link to="/inference">inference configuration</Link> page, which an account administrator
        manages.
      </p>
    </div>
  );
}
